/**
 * AdminCoverageQaTask - Admin view for reviewing completed COVERAGE_REVIEW_QA tasks.
 *
 * Displays both:
 * 1. The original labeler work (box + product selection from CoverageTaskResult)
 * 2. The QA reviewer feedback (CoverageQaResult: approved/corrected/rejected)
 *
 * @route /jobs/:jobId/admin-coverage-qa
 */
import { useEffect, useMemo, useRef, useState } from "react";
import { useParams, useSearchParams, useNavigate } from "react-router-dom";
import {
  useFetchJobTasks,
  useFetchTask,
  useFetchParcel,
  useImageGallery,
} from "@labeling-tools/api/lebron/react-query-hooks";
import { Badge, LoadingOverlay, Text } from "@mantine/core";
import { ReactZoomPanPinchRef } from "react-zoom-pan-pinch";
import { BoxDrawImage } from "@labeling-tools/ui-components/src/box-draw-image";
import { PlanogramViewer } from "@labeling-tools/ui-components/src/planogram-viewer/planogram-viewer.comp";
import { useAtlasPlanogram } from "@labeling-tools/api/hooks";
import { AdminToolbar } from "../components/AdminToolbar";
import { CoverageSidebar, COVERAGE_SIDEBAR_WIDTH } from "@/components/coverage/CoverageSidebar";
import { CoverageQaResultsSidebar } from "@/components/coverage/CoverageQaResultsSidebar";
import { extractBoxPoints, extractOriginalProductSelection } from "@/components/coverage/useCoverageQaData";
import { HEADER_HEIGHT, RESULTS_SIDEBAR_WIDTH } from "@/config/data";
import type {
  CoverageQaResult,
  ProductSelection,
} from "@/types/coverage";

export const AdminCoverageQaTask = () => {
  const navigate = useNavigate();
  const { jobId } = useParams();
  const [searchParams] = useSearchParams();
  const taskId = searchParams.get("taskId");
  const zoomRef = useRef<ReactZoomPanPinchRef>(null);

  // Fetch job tasks and active task
  const { data: jobTasks, isLoading: jobTasksLoading } = useFetchJobTasks(
    jobId || ""
  );
  const { data: qaTask, isLoading: qaTaskLoading } = useFetchTask(
    jobId || "",
    taskId || jobTasks?.tasks[0]?.id
  );

  // Extract task data and result
  const taskData = qaTask?.data as
    | {
        parcel_id?: string;
        image_id?: string;
        original_task_id?: string;
        original_result?: {
          box?: { points: number[][] };
          product_selection?: ProductSelection;
          empty_areas?: Array<{ points: number[][] }>;
          product_selections?: ProductSelection[];
        };
      }
    | undefined;

  const qaResult = qaTask?.result as CoverageQaResult | undefined;

  // Fetch parcel data (may be needed for extended metadata later)
  const { isLoading: parcelLoading } = useFetchParcel(taskData?.parcel_id);

  // Fetch shelf image
  const {
    mutate: mainImageMutation,
    data: mainImageGallery,
    isPending: mainImageLoading,
    reset: resetImageGallery,
  } = useImageGallery();

  const shelfImageUrl = mainImageGallery?.images[0]?.image;
  const imageIdToFetch = (qaTask?.image_id ?? taskData?.image_id) as string | undefined;

  // Reset stale image data and fetch the new task's image when navigating
  useEffect(() => {
    resetImageGallery();
    if (imageIdToFetch) {
      mainImageMutation({ image_ids: [imageIdToFetch] });
    }
  }, [imageIdToFetch, mainImageMutation, resetImageGallery]);

  // Default to first task if no taskId in URL
  useEffect(() => {
    if (qaTask && !taskId && jobTasks?.tasks[0]?.id) {
      navigate(
        `/jobs/${jobId}/admin-coverage-qa?taskId=${jobTasks.tasks[0].id}`,
        { replace: true }
      );
    }
  }, [qaTask, taskId, jobId, jobTasks, navigate]);

  // Extract box points and product selection from original result
  const boxPoints = useMemo(() => extractBoxPoints(taskData?.original_result), [taskData?.original_result]);
  const originalProductSelection = useMemo(() => extractOriginalProductSelection(taskData?.original_result), [taskData?.original_result]);

  // Determine which planogram to show
  const originalPlanogramId = originalProductSelection?.planogram_id ?? "";
  const correctedPlanogramId =
    qaResult?.status === "corrected" ? qaResult.corrected_planogram_id : undefined;
  const activePlanogramId = correctedPlanogramId || originalPlanogramId;

  // Fetch planogram data
  const { data: planogramData, isLoading: planogramLoading } =
    useAtlasPlanogram(activePlanogramId);

  // Determine which product IDs to highlight
  const originalProductIds = originalProductSelection?.product_ids ?? [];
  const correctedProductIds =
    qaResult?.status === "corrected" ? qaResult.corrected_product_ids : [];
  const displayProductIds =
    correctedProductIds.length > 0 ? correctedProductIds : originalProductIds;

  // When corrected, show original products with yellow border
  const warnedProductIds = useMemo(() => {
    if (correctedProductIds.length === 0) return [];
    return originalProductIds;
  }, [originalProductIds, correctedProductIds]);

  // Build product name map from planogram
  const productNameMap = useMemo(() => {
    const map = new Map<string, string>();
    if (planogramData?.products) {
      for (const product of planogramData.products) {
        map.set(product.product_id, product.description || "Unknown product");
      }
    }
    return map;
  }, [planogramData?.products]);

  // Build product image URLs map
  const productImageUrls = useMemo(() => {
    const map = new Map<string, string>();
    if (planogramData?.products) {
      for (const product of planogramData.products) {
        if (product.image_url) {
          map.set(product.product_id, product.image_url);
        }
      }
    }
    return map;
  }, [planogramData?.products]);

  // UI state — reset when navigating between tasks
  const [brightness, setBrightness] = useState(100);
  const [boxesVisible, setBoxesVisible] = useState(true);
  const [resetViewKey, setResetViewKey] = useState(0);

  useEffect(() => {
    setBrightness(100);
    setBoxesVisible(true);
    setResetViewKey((k) => k + 1);
  }, [taskId]);

  // Build box overlay for BoxDrawImage
  const existingBoxes = useMemo(() => {
    if (!boxPoints) return [];
    return [{ points: boxPoints }];
  }, [boxPoints]);

  // Box image overlay showing selected product
  const boxImageOverlays = useMemo(() => {
    const map = new Map<number, string>();
    if (displayProductIds.length > 0) {
      const imageUrl = productImageUrls.get(displayProductIds[0]);
      if (imageUrl) map.set(0, imageUrl);
    }
    return map;
  }, [displayProductIds, productImageUrls]);

  // Box product labels for tooltip
  const boxProductLabels = useMemo(() => {
    const map = new Map<number, { name: string; imageUrl?: string }>();
    if (displayProductIds.length > 0) {
      const count = displayProductIds.length;
      const firstName =
        productNameMap.get(displayProductIds[0]) ?? "Selected product";
      const name = count > 1 ? `${firstName} (+${count - 1} more)` : firstName;
      map.set(0, {
        name,
        imageUrl: productImageUrls.get(displayProductIds[0]),
      });
    }
    return map;
  }, [displayProductIds, productNameMap, productImageUrls]);

  const isLoading =
    jobTasksLoading || qaTaskLoading || parcelLoading || mainImageLoading;

  if (isLoading) {
    return <LoadingOverlay visible={true} />;
  }

  const hasResult = !!qaResult;

  return (
    <div style={{ paddingTop: HEADER_HEIGHT }}>
      {/* Admin toolbar */}
      {jobTasks && qaTask && (
        <AdminToolbar
          jobTasks={jobTasks}
          activeTask={qaTask}
          taskLabelFn={(task, index, total) => {
            const qaStatus = (task.result as any)?.status as string | undefined;
            const suffix = qaStatus ? ` | ${qaStatus.toUpperCase()}` : "";
            return `[${index + 1} / ${total}] ${task.id?.slice(task.id.length - 6)} | ${task.status}${suffix}`;
          }}
        />
      )}

      {/* Left sidebar - View controls only */}
      <CoverageSidebar
        drawMode="cursor"
        onDrawModeChange={() => {}}
        brightness={brightness}
        onBrightnessChange={setBrightness}
        boxesVisible={boxesVisible}
        onToggleBoxVisibility={() => setBoxesVisible((v) => !v)}
        onResetView={() => {
          setResetViewKey((k) => k + 1);
          setBrightness(100);
        }}
        onZoomIn={() => zoomRef.current?.zoomIn(0.5)}
        onZoomOut={() => zoomRef.current?.zoomOut(0.5)}
        readOnly
      />

      {/* Main content area */}
      <div
        className="absolute flex"
        style={{
          top: HEADER_HEIGHT * 2,
          left: COVERAGE_SIDEBAR_WIDTH,
          right: hasResult ? RESULTS_SIDEBAR_WIDTH : 0,
          bottom: 0,
        }}
      >
        {/* Left panel: Shelf Image */}
        <div className="relative flex-1 min-w-0">
          {shelfImageUrl && (
            <div
              style={{ filter: `brightness(${brightness}%)` }}
              className="w-full h-full flex justify-center items-center"
            >
              <BoxDrawImage
                key={resetViewKey}
                src={shelfImageUrl}
                alt="Coverage QA Admin View"
                existingBoxes={boxesVisible ? existingBoxes : []}
                hiddenExistingBoxIndices={new Set()}
                selectedExistingBoxIndex={0}
                onSelectedExistingBoxChange={() => {}}
                userDrawnBoxes={[]}
                hiddenUserDrawnBoxIndices={new Set()}
                onUserDrawnBoxesChange={() => {}}
                selectedUserDrawnBoxIndex={null}
                onSelectedUserDrawnBoxChange={() => {}}
                defaultMode="pan"
                variant="empty"
                enableZoom
                enableDrawing={false}
                enableDeletion={false}
                showModeControls={false}
                zoomRef={zoomRef}
                boxImageOverlays={boxImageOverlays}
                useNumbers
                boxNumberLabels={new Map([[0, 1]])}
                confirmedBoxIndices={new Set([0])}
                boxProductLabels={boxProductLabels}
              />
            </div>
          )}
        </div>

        {/* Right panel: Planogram */}
        <div className="flex-1 min-w-0 bg-white border-l border-gray-200 flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-2 border-b border-gray-200">
            <Text fw={600} size="sm">
              Planogram
            </Text>
            {correctedPlanogramId && (
              <Badge size="sm" color="yellow">
                Corrected
              </Badge>
            )}
          </div>

          {/* Product selection info */}
          {displayProductIds.length > 0 && (
            <div
              className={`px-4 py-2 border-b ${
                correctedProductIds.length > 0
                  ? "bg-yellow-50 border-yellow-200"
                  : "bg-blue-50 border-blue-200"
              }`}
            >
              <Text
                size="xs"
                c={correctedProductIds.length > 0 ? "yellow" : "blue"}
                fw={600}
              >
                {displayProductIds.length} product
                {displayProductIds.length !== 1 ? "s" : ""} selected
                {correctedProductIds.length > 0 && " (corrected)"}
              </Text>
            </div>
          )}

          {/* Planogram viewer */}
          <div className="flex-1 min-h-0">
            {planogramLoading ? (
              <div className="flex items-center justify-center h-full">
                <LoadingOverlay visible />
              </div>
            ) : activePlanogramId ? (
              <div className="h-full [&>div]:!h-full">
                <PlanogramViewer
                  planogram_id={activePlanogramId}
                  fitContent
                  selectedProductIds={displayProductIds}
                  warnedProductIds={warnedProductIds}
                />
              </div>
            ) : (
              <div className="flex items-center justify-center h-full">
                <Text c="dimmed" size="sm">
                  No planogram available
                </Text>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* QA Results Sidebar */}
      {hasResult && (
        <CoverageQaResultsSidebar
          qaResult={qaResult}
          qaTask={qaTask}
          originalProductIds={originalProductIds}
          productNameMap={productNameMap}
          productImageUrls={productImageUrls}
        />
      )}
    </div>
  );
};
